/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type ElementRef, Injectable } from '@angular/core';
import {
    type AdaptResult, type BaseRequestOptionsArgs, type FormControl, type FormGroup,
    type HttpResponse, Consts, EventService, ExtendService, HttpService, I18nService, LocalStorageService, PermissionService, Rest,
    UNPLACEHOLDER, UtilService,
} from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { type OEM } from 'app/common/types/oem';
import { FeatureConfig } from 'app/config/feature';
import { type ContainerServiceStatusInfo } from 'app/feature/settings/container-configuration/container-configuration.model';
import { ApplicationAppTypeE, ApplicationRunningStatusE } from 'app/feature/system/smart-container/applications/applications.const';
import { ContainerServiceStatus } from 'app/feature/system/smart-container/repository/repository.enum';
import { cloneDeep, divide, floor } from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, flatMap, map } from 'rxjs/operators';

import { healthStatusE, runningStatusE, STORAGE_KEY } from '../constants';
import { LicenseKey } from '../constants/license-key';
import { productModelE } from '../constants/product-model';
import { type KeyValue } from '../types/key-value';
import { type Applicaion, type PmsStatus, type RemoteDevice } from '../types/mo';
import { GlobalDataService } from './global-data.service';
import { RemoteExecuteService } from './remote-execute.service';

export interface GetAllDataOptions extends BaseRequestOptionsArgs {
    /**
     * 远端设备ID。通过该参数可以查询远端设备的数据
     */
    remoteDeviceId?: number;
    /**
     * 每批次查询数据的大小，默认4K
     */
    pageSize?: number;
}

// 容量列表，当前最大支持到EB
const capacityUnitList = [Consts.BYTE, Consts.KB, Consts.MB, Consts.GB, Consts.TB, Consts.PB, Consts.EB];

export const HOURS_PER_DAY = 24;

let timerWorker;
const optionsMap: any = {};

@Injectable({ providedIn: 'root' })
export class ToolService {
    oem: OEM = (window as any).oem;

    constructor(
        private i18nService: I18nService,
        private http: HttpService,
        private remoteExe: RemoteExecuteService,
        private utilService: UtilService,
        private globalDataService: GlobalDataService,
        private extend: ExtendService,
        private permissionService: PermissionService,
        private localStorageService: LocalStorageService,
        private eventService: EventService,
    ) { }

    /**
     * 自适应计算容量单位
     * @param {string|number} oriCapacityValue 待计算容量大小
     * @param {string} unit 当前单位，默认值MB
     * @param {string} oriTargetUnit 转换到目标单位，如果为空则自动计算
     * @param {number} sectorSize 扇区转化byte单位
     * @param {number} sectorUnit 标称容量转换单位为1000  其他为1024
     * @returns {object} 字符串字节长度
     * @memberof UtilService
     */
    adaptUnit2(oriCapacityValue: string | number, unit: string = Consts.MB,
        oriTargetUnit?: string, sectorSize = 512, sectorUnit = 1024): AdaptResult {
        let capacityValue = oriCapacityValue;
        let targetUnit = oriTargetUnit;

        capacityValue = Number(capacityValue);

        const returnObj = this.getAdaptReturnObj(capacityValue, unit);

        if (isNaN(capacityValue)) {
            return returnObj;
        }

        if (returnObj.value === 0) {
            returnObj.unit = targetUnit || Consts.MB;
        } else if (targetUnit !== returnObj.unit) {
            // 源单位为扇区时，转换为Byte计算
            if (returnObj.unit === Consts.SECTOR) {
                returnObj.scale *= sectorSize;
                returnObj.value = returnObj.value * sectorSize;
                returnObj.unit = Consts.BYTE;
            }

            // 转到指定单位
            if (targetUnit) {
                // 目标单位为扇区时，转换为Byte计算
                const isTargetSector = targetUnit === Consts.SECTOR;
                if (isTargetSector) {
                    targetUnit = Consts.BYTE;
                }
                const index = capacityUnitList.indexOf(returnObj.unit);
                const targetIndex = capacityUnitList.indexOf(targetUnit);
                returnObj.scale *= Math.pow(sectorUnit, index - targetIndex);
                returnObj.value = returnObj.value * Math.pow(sectorUnit, index - targetIndex);
                if (isTargetSector) {
                    returnObj.scale /= sectorSize;
                    returnObj.value = returnObj.value / sectorSize;
                    returnObj.unit = Consts.SECTOR;
                } else {
                    returnObj.unit = targetUnit;
                }
            } else { // 自动计算目标单位
                // 转换为Byte进行计算
                const byteTimes = capacityUnitList.indexOf(returnObj.unit);
                returnObj.scale *= Math.pow(sectorUnit, byteTimes);
                returnObj.value = returnObj.value * Math.pow(sectorUnit, byteTimes);
                returnObj.unit = Consts.BYTE;
                // 转换到大于0小于1024(标称容量为1000)的值对应的单位，最大不超过DB
                while (returnObj.value >= sectorUnit && Consts.DB !== returnObj.unit) {
                    returnObj.scale /= sectorUnit;
                    returnObj.value = divide(returnObj.value, sectorUnit);
                    returnObj.unit = this.utilService.getNextCapacityUnit(returnObj.unit);
                }
            }
        }

        returnObj.value = Number(floor(returnObj.value, 3));
        const key = returnObj.value.toFixed(3);
        returnObj.label = `${key} ${returnObj.unit}`;

        return returnObj;
    }

    getAdaptReturnObj(capacityValue: number, unit: string): AdaptResult {
        return {
            value: capacityValue,
            unit,
            scale: 1,
            originalValue: capacityValue,
            originalUnit: unit,
            label: UNPLACEHOLDER,
        };
    }

    /**
     * 从keyValue中获取selectionOption
     * @param kv
     * @param values
     */
    getKeyValeOptions(kv: KeyValue, values?: Array<number | string>): SelectOption[] {
        const options: SelectOption[] = [];

        if (values) {
            for (const value of values) {
                for (const k of Object.keys(kv)) {
                    const v = kv[k];
                    if (value === v.value) {
                        options.push({
                            label: this.i18nService.get(v.i18n),
                            value: String(v.value),
                        });
                    }
                }
            }
        } else {
            for (const k of Object.keys(kv)) {
                const v = kv[k];
                options.push({
                    label: this.i18nService.get(v.i18n),
                    value: String(v.value),
                });
            }
        }

        return options;
    }

    /**
     * 将枚举值转换为选项列表
     * @param enumOptions 原始选项列表
     * @param values 枚举值列表，如果指定的话则仅返回指定的枚举值选项列表
     */
    convertEnumToOptions<T>(enumOptions: SelectOption[], values?: T[]): SelectOption[] {
        const options: SelectOption[] = [];

        for (const opt of enumOptions) {
            if (!values || values.includes(opt.value)) {
                options.push({
                    value: opt.value,
                    label: this.i18nService.get(opt.label),
                });
            }
        }

        return options;
    }

    /**
     * 获取指定枚举值的文本
     */
    getLabelFromEnumOptions<T>(enumOptions: SelectOption[], value?: T): string {
        for (const opt of enumOptions) {
            if (value === opt.value) {
                return this.i18nService.get(opt.label);
            }
        }
        return UNPLACEHOLDER;
    }

    /**
     * 分批次请求全部数据
     * @param rest 请求的Rest对象
     * @param oriCountRest 查询请求数据数量的Rest对象或者选项（可选）
     * @param oriOptions 选项，继承自BaseRequestOptionsArgs，增加了针对getAllData的选项，详见GetAllDataOptions（可选）
     * @param queryRange 每条请求查询的最大范围（默认是512）
     */
    getAllData(rest: Rest, oriCountRest?: Rest | GetAllDataOptions, oriOptions?: GetAllDataOptions, queryRange = 512): Observable<any> {
        let countRest = oriCountRest;
        let options = oriOptions;
        if (countRest && !(countRest instanceof Rest)) {
            options = countRest;
            countRest = undefined;
        }
        options = {
            pageSize: queryRange,
            ...options,
        };

        // 如果没有传入countRest，生成一个
        const finalCountRest: Rest = (countRest as Rest) || this.getCountRest(rest);

        // 根据是否传入远端设备ID判断是否是远端执行
        const remote = typeof options.remoteDeviceId === 'number';

        const countReq$ = remote ?
            this.remoteExe.get(options.remoteDeviceId, finalCountRest, options) :
            this.http.get(finalCountRest, options);

        return countReq$.pipe(
            flatMap(({ data }) => {
                const count = Number(data.COUNT || data.count);
                return this.getData(rest, count, options);
            }),
        );
    }

    /**
     * 是否为非法数字
     * @param num 字符串数字
     * @param is64Bit 底层是否为64位数字，默认不是
     */
    isInvalidNum(num: string, is64Bit = false): boolean {
        if (num === '-1') {
            return true;
        }

        if (!is64Bit) {
            return num === '4294967295';
        } else {
            return num === '18446744073709551615';
        }
    }

    /**
     * 获取坐标轴最大值平均分段后适配的最大值
     * @param value
     * @param splitNumber
     */
    getChartMaxValue(value: number, splitNumber: number): number {
        const e = Number(value.toExponential().split('e')[1]);
        const factor = Math.pow(10, e > 1 ? e - 1 : e);
        let step = factor * splitNumber;
        let maxValue = value;

        // 保证step的值的小数位正常
        if (e < 0) {
            step = Number(step.toFixed(-e));
        }

        if (value % step !== 0) {
            maxValue = value - (value % step) + step;
        }

        return maxValue;
    }

    /**
     * 获取数字占的宽度
     * 一个数字占10px，再加上每一个分隔符5px。需要多加一个数字长度，防止echarts纵坐标跳变加一位数，如99变成100
     * @param value
     */
    getNumberWidth(value: number): number {
        let str: string;
        if (value >= 3) { // 大于等于3的全部按照整数处理
            str = Math.ceil(value).toString();
        } else if (value >= 1 || value === 0) {
            str = '1.0';
        } else { // 小于0的按照科学计数法后的长度处理
            str = (Number(value.toExponential(0))).toString();
        }
        const len = str.length + 1;
        return (len * 8) + (Math.floor(len / 3) * 5);
    }

    /**
     * 是否支持license显示已使用容量
     * @returns 返回值为布尔值时表示已经确定产品型号，返回undefined时表示还未获取到产品型号，不确定是否为高端
     */
    isProdSupportLicenseUsage(): boolean | undefined {
        const systemInfo = this.globalDataService.getSystemInfo();

        if (!systemInfo) {
            return undefined;
        }

        const modes = [
            productModelE.V6_18500_837.value,
            productModelE.V6_18800_838.value,
            productModelE.V6_18500K_840.value,
        ];

        return modes.some((item) => item === Number(systemInfo.PRODUCTMODE));
    }

    /**
     * 是否为高端产品
     * @returns 返回值为布尔值时表示已经确定产品型号，返回undefined时表示还未获取到产品型号，不确定是否为高端
     */
    isProdHigh(): boolean | undefined {
        const systemInfo = this.globalDataService.getSystemInfo();

        if (!systemInfo) {
            return undefined;
        }

        const modes = [
            productModelE.V6_6800_829.value,
            productModelE.V6_8000_815.value,
            productModelE.V6_8000_816.value,
            productModelE.V6_8000_823.value,
            productModelE.V6_18000_817.value,
            productModelE.V6_18000_818.value,
            productModelE.V6_18000_824.value,
            productModelE.V6_18500_830.value,
            productModelE.V6_18800_831.value,
            productModelE.V6_18800_832.value,
            productModelE.V6_6800_917.value,
            productModelE.V6_8000_904.value,
            productModelE.V6_8000_905.value,
            productModelE.V6_8000_911.value,
            productModelE.V6_18000_906.value,
            productModelE.V6_18000_907.value,
            productModelE.V6_18000_912.value,
            productModelE.V6_18500_918.value,
            productModelE.V6_18800_919.value,
            productModelE.V6_18800_920.value,
            productModelE.V6_x9000_1201.value,
            productModelE.V6_A9000_1203.value,
            productModelE.V6_6800_836.value,
            productModelE.V6_18500_837.value,
            productModelE.V6_18800_838.value,
            productModelE.V6_18500K_840.value,
            productModelE.V6_X9000K_1207.value,
        ];
        return modes.some((item) => item === Number(systemInfo.PRODUCTMODE));
    }

    /**
     * 是否为中端产品
     * @returns 返回值为布尔值时表示已经确定产品型号，返回undefined时表示还未获取到产品型号，不确定是否为高端
     */
    isProdMiddle(): boolean | undefined {
        const systemInfo = this.globalDataService.getSystemInfo();

        if (!systemInfo) {
            return undefined;
        }

        const modes = [
            productModelE.V3_5000_805.value,
            productModelE.V3_6000_806.value,
            productModelE.V6_5000_811.value,
            productModelE.V6_5000_812.value,
            productModelE.V6_6000_813.value,
            productModelE.V6_6000_814.value,
            productModelE.V6_5000_821.value,
            productModelE.V6_6000_822.value,
            productModelE.V6_5000_900.value,
            productModelE.V6_5000_901.value,
            productModelE.V6_6000_902.value,
            productModelE.V6_6000_903.value,
            productModelE.V6_5000_909.value,
            productModelE.V6_6000_910.value,
            productModelE.V6_x8000_1200.value,
            productModelE.V6_A8000_1202.value,
            productModelE.V6_5500_834.value,
            productModelE.V6_5510_924.value,
            productModelE.V6_A300_926.value,
            productModelE.V6_5600_835.value,
            productModelE.V6_5600k_839.value,
            productModelE.V6_5600k_921.value,
            productModelE.V6_5000_841.value,
            productModelE.V6_6000_842.value,
            productModelE.V6_5810H_843.value,
            productModelE.V6_A6000_1204.value,
            productModelE.V6_X6000_1205.value,
            productModelE.V6_X8000K_1206.value,
            productModelE.V5_5510S_850.value,
            productModelE.V6_X3000_852.value,
            productModelE.V6_L8000_1208.value,
            productModelE.V7_E8000_1209.value,
        ];
        return modes.some((item) => item === Number(systemInfo.PRODUCTMODE));
    }

    /**
     * 是否为低端产品
     */
    isProdLow(): boolean | undefined {
        const systemInfo = this.globalDataService.getSystemInfo();

        if (!systemInfo) {
            return undefined;
        }

        const modes = [
            productModelE.V6_3000_819.value,
            productModelE.V6_5300_825.value,
            productModelE.V6_3000_908.value,
            productModelE.V6_5300_913.value,
            productModelE.V6_5310_833.value,
            productModelE.V6_5310_923.value,
            productModelE.V6_2910_848.value,
            productModelE.V6_5320_849.value,
            productModelE.V6_2200_853.value,
            productModelE.V6_2220_854.value,
            productModelE.V6_2600_855.value,
            productModelE.V6_5110_856.value,
            productModelE.V6_5120_857.value,
            productModelE.V6_5210_858.value,
            productModelE.V6_5220_859.value,
            productModelE.V6_2620_861.value,
            productModelE.V6_2000_851.value,
            productModelE.V6_2100_922.value,
            productModelE.V6_2020_925.value,
        ];

        return modes.some((item) => item === Number(systemInfo.PRODUCTMODE));
    }

    /**
     * 是否为入门级存储，默认进极简模式
     * @param productMode 产品型号
     */
    isProdEntryLevel(productMode?: string): boolean | undefined {
        const systemInfo = this.globalDataService.getSystemInfo();

        if (!systemInfo && !productMode) {
            return undefined;
        }

        const modes = [
            productModelE.V6_2000_851.value,
            productModelE.V6_2200_853.value,
            productModelE.V6_2220_854.value,
            productModelE.V6_2600_855.value,
            productModelE.V6_5110_856.value,
            productModelE.V6_5120_857.value,
            productModelE.V6_5210_858.value,
            productModelE.V6_5220_859.value,
            productModelE.V6_2620_861.value,
            productModelE.V6_2100_922.value,
            productModelE.V6_2020_925.value,
        ];

        const model = productMode ? productMode : systemInfo.PRODUCTMODE;
        return modes.some((item) => item === Number(model));
    }

    /**
     * 是否为支持极简模式的低端型号。默认不进入极简（53系列、Dorado 3000）
     * @param productMode 产品型号
     */
    isProdSimpleModeLowLevel(productMode?: string): boolean | undefined {
        const systemInfo = this.globalDataService.getSystemInfo();

        if (!systemInfo && !productMode) {
            return undefined;
        }

        const modes = [
            productModelE.V6_3000_819.value,
            productModelE.V6_5300_825.value,
            productModelE.V6_3000_908.value,
            productModelE.V6_5300_913.value,
            productModelE.V6_5300K_844.value,
            productModelE.V6_5310_833.value,
            productModelE.V6_5310_923.value,
            productModelE.V6_5320_849.value,
        ];

        const model = productMode ? productMode : systemInfo.PRODUCTMODE;
        return modes.some((item) => item === Number(model));
    }

    /**
     * 是否支持极简模式
     * @param productMode 系统型号
     */
    isSupportSimpleMode(productMode?: string): boolean {
        return Boolean(this.isProdEntryLevel(productMode)) || Boolean(this.isProdSimpleModeLowLevel(productMode));
    }

    /**
     * 是否为OceanProtect产品
     * 修改这里需要知会omtask适配！
     * @param productMode 如果传入，则判断此型号是否属于OceanProtect产品，否则对本端设备型号进行判断
     */
    isOceanProtect(productMode?: string): boolean | undefined {
        const systemInfo = this.globalDataService.getSystemInfo();

        if (!systemInfo && !productMode) {
            return undefined;
        }

        const modes = [
            productModelE.V6_x8000_1200.value,
            productModelE.V6_x9000_1201.value,
            productModelE.V6_A8000_1202.value,
            productModelE.V6_A9000_1203.value,
            productModelE.V6_A6000_1204.value,
            productModelE.V6_X6000_1205.value,
            productModelE.V6_X8000K_1206.value,
            productModelE.V6_X9000K_1207.value,
            productModelE.V6_X3000_852.value,
            productModelE.V6_L8000_1208.value,
            productModelE.V7_E8000_1209.value,
        ];

        const model = productMode ? productMode : systemInfo.PRODUCTMODE;
        return modes.some((item) => item === Number(model));
    }

    /**
     * 是否为OceanProtect X系列产品
     * 修改这里需要知会omtask适配！
     */
    isOceanProtectXseries(): boolean | undefined {
        const systemInfo = this.globalDataService.getSystemInfo();

        if (!systemInfo) {
            return undefined;
        }

        const modes = [
            productModelE.V6_x8000_1200.value,
            productModelE.V6_x9000_1201.value,
            productModelE.V6_X6000_1205.value,
            productModelE.V6_X8000K_1206.value,
            productModelE.V6_X9000K_1207.value,
            productModelE.V6_X3000_852.value,
            productModelE.V6_L8000_1208.value,
            productModelE.V7_E8000_1209.value,
        ];
        return modes.some((item) => item === Number(systemInfo.PRODUCTMODE));
    }

    /**
     * 是否为OceanProtect X8000产品
     * 修改这里需要知会omtask适配！
     * X6000，X8000K与x8000表现一致（除名称图片外）
     */
    isOceanProtectX8000(): boolean | undefined {
        const systemInfo = this.globalDataService.getSystemInfo();

        if (!systemInfo) {
            return undefined;
        }

        const modes = [
            productModelE.V6_x8000_1200.value,
            productModelE.V6_X8000K_1206.value,
            productModelE.V6_X6000_1205.value,
            productModelE.V6_X3000_852.value, // 此型号功能和x6000一致
            productModelE.V6_L8000_1208.value,
            productModelE.V7_E8000_1209.value,
        ];
        return modes.some((item) => item === Number(systemInfo.PRODUCTMODE));
    }

    /**
     * 新融合系列产品
     */
    isCoverageStor(): boolean | undefined {
        const systemInfo = this.globalDataService.getSystemInfo();
        if (!systemInfo) {
            return undefined;
        }
        const modes = [
            productModelE.V6_5310_833.value,
            productModelE.V6_5310_923.value,
            productModelE.V6_5500_834.value,
            productModelE.V6_5510_924.value,
            productModelE.V6_A300_926.value,
            productModelE.V6_5600_835.value,
            productModelE.V6_6800_836.value,
            productModelE.V6_18500_837.value,
            productModelE.V6_18800_838.value,
            productModelE.V6_18500K_840.value,
            productModelE.V6_5000_841.value,
            productModelE.V6_6000_842.value,
            productModelE.V6_5810H_843.value,
            productModelE.V6_5300K_844.value,
            productModelE.V6_5500K_845.value,
            productModelE.V6_2910_848.value,
            productModelE.V6_5320_849.value,
            productModelE.V5_5510S_850.value,
            productModelE.V6_2200_853.value,
            productModelE.V6_2220_854.value,
            productModelE.V6_2600_855.value,
            productModelE.V6_5110_856.value,
            productModelE.V6_5120_857.value,
            productModelE.V6_5210_858.value,
            productModelE.V6_5220_859.value,
            productModelE.V6_2620_861.value,

        ];
        return modes.some((item) => item === Number(systemInfo.PRODUCTMODE));
    }

    /**
     * 新融合5510S型号产品
     * [5510S型号不支持HDD盘：屏蔽存储池容量层相关]
     */
    is5510SModel(): boolean | undefined {
        const systemInfo = this.globalDataService.getSystemInfo();
        if (!systemInfo) {
            return undefined;
        }
        const modes = [
            productModelE.V5_5510S_850.value,
        ];
        return modes.some((item) => item === Number(systemInfo.PRODUCTMODE));
    }

    /**
     * k系列产品
     */
    isKseries(): boolean | undefined {
        const systemInfo = this.globalDataService.getSystemInfo();
        if (!systemInfo) {
            return undefined;
        }
        const modes = [
            productModelE.V6_18800_832.value,
            productModelE.V6_18800_920.value,
            productModelE.V6_18500K_840.value,
        ];
        return modes.some((item) => item === Number(systemInfo.PRODUCTMODE));
    }

    /**
     * OceanProtect A系列产品
     * 修改这里需要知会omtask适配！
     * @returns 返回undefined时表示还未获取到产品型号，产品型号为OceanProtect A系列返回true，其他产品型号返回false
     */
    isOceanProtectAseries(): boolean | undefined {
        const systemInfo = this.globalDataService.getSystemInfo();

        if (!systemInfo) {
            return undefined;
        }

        const modes = [
            productModelE.V6_A8000_1202.value,
            productModelE.V6_A9000_1203.value,
            productModelE.V6_A6000_1204.value,
        ];
        return modes.some((item) => item === Number(systemInfo.PRODUCTMODE));
    }

    /**
     * OceanStor Dorado 2000产品
     */
    isDorado2000(): boolean | undefined {
        const systemInfo = this.globalDataService.getSystemInfo();

        if (!systemInfo) {
            return undefined;
        }

        const modes = [
            productModelE.V6_2000_851.value,
        ];

        return modes.some((item) => item === Number(systemInfo.PRODUCTMODE));
    }

    /**
     * 查询产品型号名称
     * @param productModel 型号枚举
     */
    getProductModelName = (productModel: number): string => {
        if (this.isOem()) {
            return this.oem.productModel;
        }

        if (!productModel) {
            return '';
        }

        const pro = Object.values(productModelE).find(({ value }) => value === Number(productModel));
        return pro ? this.i18nService.get(pro.i18n) : UNPLACEHOLDER;
    };

    /**
     * 将十进制数字字符串转换为16进制。支持超大数字
     * @param num
     */
    decToHex(num: number | string): string {
        // 将数字拆分为单个数字的数组
        const nums = num.toString().split('');
        // 保存对应十六进制数的十进制数的数组，注意是倒序的
        const sum: number[] = [];
        // 保存每一位十六进制数字
        const hex: string[] = [];

        while (nums.length) {
            let s = Number(nums.shift());

            for (let i = 0; s || i < sum.length; i++) {
                s += (sum[i] || 0) * 10;
                sum[i] = s % 16;
                s = (s - sum[i]) / 16;
            }
        }

        // 将每一位十进制转换为16进制，注意倒序
        while (sum.length) {
            hex.push(sum.pop().toString(16).toUpperCase());
        }

        return `0x${hex.length ? hex.join('') : '0'}`;
    }

    timeout(options: {
        name: string;
        type?: 'start' | 'stop';
        interval?: number;
        duration?: number;
        intervalFn?: (time) => boolean;
        endFn?: () => void;
    }): void {
        if (!timerWorker) {
            const js = `
                var timers = {};
                self.onmessage = function (e) {
                    var options = e.data;
                    if (!options.name) {
                        return;
                    }
                    if (options.type == 'start' || !options.type) {
                        sendMessage(options);
                    } else if (options.type == 'stop') {
                        clearInterval(timers[options.name]);
                        timers[options.name] = undefined;
                    }
                };
                function sendMessage(options) {
                    var name = options.name;
                    var interval = options.interval;
                    var duration = options.duration;
                    var time = 0;
                    if (timers[name]) {
                        clearInterval(timers[name])
                    }
                    timers[name] = setInterval(function () {
                        time += interval;
                        self.postMessage({
                            name: name,
                            time: time
                        });
                    }, interval);
                }
            `;
            const blob = new Blob([js], { type: 'application/javascript' });
            timerWorker = new Worker(URL.createObjectURL(blob));
            timerWorker.onmessage = (e): void => {
                const data = e.data;
                // eslint-disable-next-line @typescript-eslint/no-shadow
                const optionsItem = optionsMap[data.name];
                if (!optionsItem) {
                    return;
                }
                if (optionsItem.intervalFn) {
                    optionsItem.intervalFn(data.time);
                }
                if (data.time >= optionsItem.duration) {
                    timerWorker.postMessage({
                        name: optionsItem.name,
                        type: 'stop',
                    });
                    if (optionsItem.endFn) {
                        optionsItem.endFn();
                    }
                    optionsMap[data.name] = undefined;
                }
            };
        }
        const timerOptions = {
            intervalFn: options.intervalFn,
            endFn: options.endFn,
            name: options.name,
            interval: options.interval || 1000,
            duration: options.duration || Infinity,
            type: options.type || 'start',
        };
        optionsMap[options.name] = timerOptions;
        timerWorker.postMessage({
            name: timerOptions.name,
            interval: timerOptions.interval,
            duration: timerOptions.duration,
            type: timerOptions.type,
        });
    }

    /**
     * 必填错误
     * @param name
     */
    requiredErr(name: string): string {
        return this.i18nService.get('common.null_validation', [name]);
    }

    /**
     * 通用名称格式错误
     * 只能包含字母、数字、“_”、“-”、“.”和中文字符
     * @param name
     */
    commonNameErr(name: string): string {
        return this.i18nService.get('common.nameformat_validation', [name]);
    }

    /**
     * ASCII通用名称格式错误
     * 只能包含字母、数字、“_”、“-”、“.”
     * @param name
     */
    asciiNameErr(name: string): string {
        return this.i18nService.get('protection.externalStorage_task_modify_name_tip', [name]);
    }

    /**
     * 严格名称格式错误
     * 字母、数字、下划线、中文字符
     * @param name
     */
    strictNameErr(name: string): string {
        return this.i18nService.get('service.filesystem_term_name_rule', [name]);
    }

    /**
     * 长度范围错误
     * @param min
     * @param max
     */
    rangeByteLengthErr(min: number, max: number): string {
        return this.i18nService.get('common.length_msg', [min, max]);
    }

    /**
     * 每条长度范围错误。用于多条输入框
     * @param min
     * @param max
     */
    everyRangeByteLengthErr(min: number, max: number): string {
        return this.i18nService.get('common.length_oneValue_msg', [min, max]);
    }

    /**
     * 取值范围错误
     * @param name
     * @param min
     * @param max
     */
    rangeErr(name: string, min: string | number, max: string | number): string {
        return this.i18nService.get('common.range_validation', [name, min, max]);
    }

    rgbToHex(color: string): string {
        if (color[0] === '#') {
            return color;
        }

        if (color.startsWith('rgb(')) {
            const arr = color.slice(4, -1).split(',').map((item) => {
                const num = item.trim();
                let numstr = parseInt(num, 10).toString(16).toUpperCase();
                if (numstr.length === 1) {
                    numstr = `0${numstr}`;
                }
                return numstr;
            });
            return `#${arr.join('')}`;
        }

        return color;
    }

    hexToRgb(color: string): string {
        if (color.startsWith('rgb(')) {
            return color;
        }

        if (color[0] === '#') {
            const arr = [];
            for (let i = 1; i < 7; i = i + 2) {
                arr.push(parseInt(color.slice(i, i + 2), 16));
            }
            return `rgb(${arr.join(', ')})`;
        }

        return color;
    }

    gradient(startColor: string, endColor: string, rate: number): string {
        if (rate <= 0) {
            return startColor;
        }
        if (rate >= 1) {
            return endColor;
        }

        const hexToRgbArr = (color: string): number[] => {
            // 转换成标准的rgb格式
            // eslint-disable-next-line @typescript-eslint/no-shadow
            const rgbColor = this.hexToRgb(color);
            return rgbColor.slice(4, -1).split(', ').map((item) => Number(item));
        };

        const startArr = hexToRgbArr(startColor);
        const endArr = hexToRgbArr(endColor);

        const newArr = startArr.map((item, i) => ((endArr[i] - item) * rate) + item);

        const rgbColor = `rgb(${newArr.join(', ')})`;
        if (startColor.startsWith('rgb(')) {
            return rgbColor;
        } else {
            return this.rgbToHex(rgbColor);
        }
    }

    /**
     * 返回硬盘剩余寿命月数，小于1个月返回"< 1"，大于60个月返回"> 60"
     * @param days 剩余寿命（天）
     */
    diskRemainLife(days: string): string {
        if (!days || Number(days) === -1) {
            return UNPLACEHOLDER;
        }
        const month = Math.floor(Number(days) / 30);

        if (month > 60) {
            return '> 60';
        } else if (month < 1) {
            return '< 1';
        } else {
            return month.toString();
        }
    }

    /**
     * 将{"numerator":1000, "denominator":1000,"logic":"="}类型的数据转换为比例
     * @param value 值
     * @param percentage 是否显示为百分比格式
     */
    getRate = (value: string, percentage?: boolean): string => {
        if (!value) {
            return UNPLACEHOLDER;
        }
        const data: { numerator: number; denominator: number; logic: string } = JSON.parse(value);
        if (!data || !Object.keys(data).length) {
            return UNPLACEHOLDER;
        }

        let denominator = Number(data.denominator);
        const numerator = Number(data.numerator);

        if (percentage) {
            let precision = (String(data.denominator)).length - 1;
            if (!denominator || !numerator) {
                return '0%';
            }
            if (precision < 0) {
                precision = 0;
            }
            const percent = numerator / denominator;
            return `${percent.toFixed(precision)}%`;
        }

        const singelNum = 10; // 单精度标示
        const doubleNum = 100; // 双精度标示
        const floatNum = 1000; // 保留三位小数
        let num = '0';
        if (denominator !== singelNum && denominator !== doubleNum && denominator !== floatNum) {
            denominator = doubleNum;
        }
        if (denominator === singelNum) {
            num = (data.numerator / denominator).toFixed(1);
        }
        if (denominator === doubleNum) {
            num = (data.numerator / denominator).toFixed(2);
        }
        if (denominator === floatNum) {
            num = (data.numerator / denominator).toFixed(3);
        }
        let label = `${num} : 1`;
        const logic = this.utilService.decodeHTML(data.logic);
        if (logic !== '=') {
            if (logic === '>=') {
                data.logic = '≥';
                label = `≥ ${label}`;
            } else if (logic === '<=') {
                label = `≤ ${label}`;
            } else {
                // ...
            }
        }
        return label;
    };

    /**
     * 是否支持thick License
     */
    supportThickLicense(): boolean {
        return this.permissionService.isLicenseExist([
            [
                LicenseKey.SmartThick,
            ],
        ]);
    }

    /**
     * 是否支持 SmartThin License
     * @returns 是否支持
     */
    supportSmartThinLicense(): boolean {
        return this.permissionService.isLicenseExist([
            [LicenseKey.SmartThin],
        ]);
    }

    /**
     * 是否支持重删压缩License
     */
    supportDedupeAndCompression(): boolean {
        // 包含其中一个License即可
        return this.permissionService.isLicenseExist([
            [
                LicenseKey.DedupeAndCompressionBasic,
                LicenseKey.DedupeAndCompressionStandard,
                LicenseKey.DedupeAndCompressionEnhanced,
                LicenseKey.DedupeAndCompressionAdvanced,
                LicenseKey.DedupeAndCompressionPremium,
                LicenseKey.DedupeAndCompressionPremiumReserved1,
                LicenseKey.DedupeAndCompressionPremiumReserved2,
                LicenseKey.DedupeAndCompressionPremiumReserved3,
                LicenseKey.DedupeAndCompressionPremiumReserved4,
                LicenseKey.DedupeAndCompressionExclusive,
                LicenseKey.SmartDedupeAndCompressionBasic,
            ],
        ]) || (FeatureConfig.IsSupportPelagodbQlcDataReductionDisplay && this.supportCovergedUniqueDataReduction());
    }

    /**
     * 是否支持新融合特有数据缩减License
     */
    supportCovergedUniqueDataReduction(): boolean {
        // 包含其中一个License即可
        return this.permissionService.isLicenseExist([
            [
                LicenseKey.DedupeAndCompressionBasicConvergedStorage,
                LicenseKey.DedupeAndCompressionExclusiveConvergedStorage,
            ],
        ]);
    }

    /**
     * 是否支持支持修改数据缩减开关
     */
    supportModifyDataReduction(): boolean {
        // 包含其中一个License即可
        return this.permissionService.isLicenseExist([
            [
                LicenseKey.DataReductionSoftware,
                LicenseKey.DedupeAndCompressionBasicConvergedStorage,
                LicenseKey.Smart_Compression,
                LicenseKey.DedupeAndCompressionExclusiveConvergedStorage,
                LicenseKey.DedupeAndCompressionBasic,
            ],
        ]) || FeatureConfig.IsSupportDataReductionWithoutLic; // 计算型存储默认支持重删压缩License，支持修改重删压缩;
    }

    /**
     * 是否支持数据缩减
     */
    supportDataReduction(): boolean {
        // 包含其中一个License即可
        return this.permissionService.isLicenseExist([
            [
                LicenseKey.DedupeAndCompressionBasic,
                LicenseKey.DedupeAndCompressionStandard,
                LicenseKey.DedupeAndCompressionEnhanced,
                LicenseKey.DedupeAndCompressionAdvanced,
                LicenseKey.DedupeAndCompressionPremium,
                LicenseKey.DedupeAndCompressionPremiumReserved1,
                LicenseKey.DedupeAndCompressionPremiumReserved2,
                LicenseKey.DedupeAndCompressionPremiumReserved3,
                LicenseKey.DedupeAndCompressionPremiumReserved4,
                LicenseKey.DedupeAndCompressionExclusive,
                LicenseKey.SmartDedupeAndCompressionBasic,
                LicenseKey.DataReductionSoftware,
                LicenseKey.DedupeAndCompressionBasicConvergedStorage,
                LicenseKey.Smart_Compression,
                LicenseKey.DedupeAndCompressionExclusiveConvergedStorage,
            ],
        ]);
    }

    /**
     * FS支持重删压缩
     */
    fsSupportDedupeAndCompression(): boolean {
        // 包含其中一个License即可
        return this.permissionService.isLicenseExist([
            [
                LicenseKey.GuaranteedCapacity,
                LicenseKey.DedupeAndCompressionBasic,
                LicenseKey.DedupeAndCompressionStandard,
                LicenseKey.DedupeAndCompressionEnhanced,
                LicenseKey.DedupeAndCompressionAdvanced,
                LicenseKey.DedupeAndCompressionPremium,
                LicenseKey.Smart_Compression,
                LicenseKey.DedupeAndCompressionPremiumReserved4,
                LicenseKey.DedupeAndCompressionExclusive,
                LicenseKey.SmartDedupeAndCompressionBasic,
                LicenseKey.DedupeAndCompressionBasicConvergedStorage,
                LicenseKey.DedupeAndCompressionExclusiveConvergedStorage,
            ],
        ]);
    }

    /**
     * 中间隐藏策略，防止名称过长。
     * @param str
     * @param keepLength 前后保留文字数
     */
    getEllipsisStr(str: string, keepLength = 3): {
        str: string;
        realStr: string;
        showEllipsis: boolean;
    } {
        let isEllipsis = false;
        const realStr = str;
        let newStr = str;
        // 截取长度规则(前后固定长度+“...”)
        if (str.length >= ((keepLength * 2) + 3)) {
            const firstStr = str.substring(0, keepLength);
            const lastStr = str.substring(str.length - keepLength);
            newStr = `${firstStr}...${lastStr}`;
            isEllipsis = true;
        }

        return {
            str: newStr,
            realStr,
            showEllipsis: isEllipsis,
        };
    }

    // 是否为OEM场景
    isOem(): boolean {
        return this.oem && this.oem.vendor !== 'huawei';
    }

    /**
     * 数组分段
     * @param array 需要分段的数组
     * @param subGroupLength 分段长度
     */
    arrayChunk(array: Array<any>, subGroupLength: number): any[] {
        let index = 0;
        const newArray = [];

        while (index < array.length) {
            newArray.push(array.slice(index, index += subGroupLength));
        }

        return newArray;
    }

    /**
     * 计算剩余时间
     */
    leftTime = (leftHours: string | number): string => {
        if (!leftHours) {
            return UNPLACEHOLDER;
        }

        const leftDay = Math.floor(Number(leftHours) / HOURS_PER_DAY);
        const leftHour = Number(leftHours) % HOURS_PER_DAY;
        return this.i18nService.get('common.validity_dayAndHour_value', [leftDay, leftHour]);
    };

    /**
     * 是否支持容器特性
     */
    hasContainerEntryLicense(): boolean | undefined {
        return this.permissionService.isLicenseExist([
            [
                LicenseKey.SmartContainerLicense,
                LicenseKey.SmartContainerLicenseStandard,
                LicenseKey.SmartContainerLicensePremium,
                LicenseKey.CloudBackup,
                LicenseKey.DataBackUpCapacity,
                LicenseKey.AntiRansomware,
                LicenseKey.ANTIRANSOMWARE_FOR_SAN,
                LicenseKey.S3,
                LicenseKey.VirtualEnvironmentEnabler,
            ],
        ]);
    }

    /**
     * 备份一体机license
     */
    hasDataBackUpCapacityLicense(): boolean | undefined {
        return this.permissionService.isLicenseExist([
            [
                LicenseKey.DataBackUpCapacity,
            ],
        ]);
    }

    /**
     * 处理CSV单元格的值，包括转义和防止注入
     * @param value
     */
    handleCsvValue(value: string | number): string {
        if (value === '' || value === null || value === undefined) {
            return UNPLACEHOLDER;
        }

        let handledValue = value;
        handledValue = handledValue.toString();
        handledValue = handledValue.replace(/"/g, '""');

        // 在最前面加一个tab键（防止excel将单元格的值识别为公式），在后面加一个tab键（处理csv显示成科学计数法的问题）
        handledValue = `\t${handledValue}\t`;

        return `"${handledValue}"`;
    }

    /**
     * 反转字符串
     * @param {string} str
     */
    reverseStr(str: string): string {
        const tempArr = str.split('');
        return tempArr.reverse().join('');
    }

    /**
     * 重置下拉框
     *
     * @param {FormGroup} vm
     * @param {string} contrlName
     */
    resetSelectControl(vm: FormGroup, contrlName: string): void {
        const selectControl = vm.get(contrlName) as FormControl;

        if (selectControl?.status === 'VALID') {
            selectControl.markAsPristine();
            selectControl.markAsUntouched();
        }

        selectControl?.patchValue('');
    }

    /**
     * 拷贝至剪切板
     * @param el 拷贝元素
     * @param str 拷贝内容
     */
    saveToClipboard(el: ElementRef, str: string): void {
        // 兼容IE
        if (!navigator.clipboard) {
            el.nativeElement.select();
            document.execCommand('copy');
            return;
        }

        navigator.clipboard.writeText(str);
    }

    /**
     * 获取顶部高度（header、质检栏和DME IQ远程申请栏、开局模式告警）
     * @param onlyHeader 是否只计算app-header的高度
     * @returns 单位是px的高度
     */
    getHeaderHeight(onlyHeader = false): string {
        const headerHeight = document.querySelector('app-header')?.getBoundingClientRect().height ?? 0;
        if (onlyHeader) {
            return `${headerHeight}px`;
        }

        const qualityInspectHeight = document.querySelector('app-quality-inspect')?.getBoundingClientRect().height ?? 0;
        const dmeIqAssistanceHeight = document.querySelector('app-remote-assistance')?.getBoundingClientRect().height ?? 0;
        // 开局模式告警与质检和远程申请栏互斥
        const deploymentModeWarningHeight = document.querySelector('app-deployment-mode')?.getBoundingClientRect().height ?? 0;
        return `${headerHeight + qualityInspectHeight + dmeIqAssistanceHeight + deploymentModeWarningHeight}px`;
    }

    /**
     * 获取左侧菜单宽度
     * @param isSimpleMode 是否是极简模式
     * @returns 单位是px的宽度
     */
    getLeftMenuWidth(isSimpleMode = false): string {
        if (isSimpleMode) {
            const simpleLeftWidth = document.querySelector('app-simple-mode .n-layout-aside')?.getBoundingClientRect().width ?? 0;
            return `${simpleLeftWidth}px`;
        }

        const leftMenuWidth = document.querySelector('n-layout .n-layout-aside')?.getBoundingClientRect().width ?? 0;
        return `${leftMenuWidth}px`;
    }

    /**
     * 将秒转换为XXX天XXX小时XXX分XXX秒格式
     * @param seconds 秒
     * @returns XXX天XXX小时XXX分XXX秒
     */
    formatSecondToDay(seconds: number): string {
        // 根据传入的秒值提取出对应的天、小时、分钟、秒
        const second = seconds % 60;
        const minute = Math.floor(seconds / 60) % 60;
        const hour = Math.floor(seconds / 3600) % 24;
        const day = Math.floor(seconds / 86400);
        const result = this.i18nService.get('service.KeepaliveTime_day', [day]) +
            this.i18nService.get('service.KeepaliveTime_hour', [hour]) +
            this.i18nService.get('service.KeepaliveTime_minute', [minute]) +
            this.i18nService.get('service.KeepaliveTime_second', [second]);

        // seconds为负数返回--
        if (seconds < 0) {
            return UNPLACEHOLDER;
        }

        // seconds为0返回"< 1秒"
        if (seconds === 0) {
            return this.i18nService.get('service.KeepaliveTime_smallerOneSecond');
        }

        return result;
    }

    /**
     * 获取额外的全局LazyLoad container容器（用于区别n-lazyload-container）
     * @param containerClassName 容器类名
     * @returns 容器元素
     */
    getFullPageLazyloadContainer(containerClassName = 'n-lazyload-container-extra'): Element {
        const appRef = document.querySelector('app-root');
        if (!appRef) {
            return null;
        }

        let container = document.body.querySelector(`.${containerClassName}`);
        if (container) {
            return container;
        }

        container = document.createElement('div');
        container.className = containerClassName;
        appRef.appendChild(container);

        return container;
    }

    /**
     * 当前是否处于360页面的懒加载容器中
     * @param [containerClassName]
     * @returns
     */
    hasInFullPageLazyloadContainer(containerClassName = 'n-lazyload-container-extra'): boolean {
        const container = document.body.querySelector(`.${containerClassName}`);
        return container && container.childElementCount > 0 ? true : false;
    }

    /**
     * 检查PMS LUN状态
     */
    checkPmsLunStatus(opt?: BaseRequestOptionsArgs): Observable<PmsStatus> {
        const rest = this.http.makeUrl('pmsLun.checkStatus', 'V2');
        return this.http.get(rest, opt).pipe(
            map(({ data }) => data),
        );
    }

    /**
     * 计算百分比
     * @param numerator 分子
     * @param denominator 分母
     * @param fixed 保留小数位数
     * @param isOverPercent 是否超过100%
     * @returns 百分比数值
     */
    getPercentValue(numerator: string, denominator: string, fixed = 2, isOverPercent = false): number {
        const numeratorNum = Number(numerator);
        const denominatorNum = Number(denominator);

        if (isNaN(numeratorNum) || isNaN(denominatorNum) || denominatorNum === 0) {
            return 0;
        }

        const fixedNum = Math.pow(10, fixed);
        let value = Math.floor(numeratorNum / denominatorNum * 100 * fixedNum) / fixedNum;

        if (!isOverPercent) {
            value = Math.min(value, 100);
        }

        return value;
    }

    /**
     * 更新阵列是否有部署防勒索应用全局标记 [控制数据保护防勒索入口是否进向导部署防勒索]
     */
    updateAntiransomwareFlag(): Observable<{ data: ContainerServiceStatusInfo }> {
        if (!this.hasContainerEntryLicense()) {
            return of(null);
        }

        return this.http.get(this.http.makeUrl('smartcontainer.summaryStatus', 'V2'), { doException: false }).pipe(
            catchError((err) => {
                this.localStorageService.set(STORAGE_KEY.ANTIRANSOMWARE_APPLICATION_CHECK, false);
                this.localStorageService.set(STORAGE_KEY.BACKUP_APPLICATION_CHECK, false);
                return of(err);
            }),
            flatMap((results: { data: ContainerServiceStatusInfo }) => {
                if (results?.data?.serviceStatus === ContainerServiceStatus.Running) {
                    return this.http.get(this.http.makeUrl('containerApplications.getBatch', 'V2'), { doException: false })
                        .pipe(
                            catchError((err) => {
                                this.localStorageService.set(STORAGE_KEY.ANTIRANSOMWARE_APPLICATION_CHECK, false);
                                this.localStorageService.set(STORAGE_KEY.BACKUP_APPLICATION_CHECK, false);
                                return of(err);
                            }),
                            flatMap(({ data }: { data: Applicaion[] }) => {
                                if (data?.length) {
                                    // 有防勒索应用并且运行状态为正常，可跳转第三方界面
                                    const antiransomwareApplicationResult = data.some((item: Applicaion) => item.appType === ApplicationAppTypeE.HYPER_DETECT &&
                                        item.status === ApplicationRunningStatusE.RUNNING);

                                    this.localStorageService.set(STORAGE_KEY.ANTIRANSOMWARE_APPLICATION_CHECK, antiransomwareApplicationResult);

                                    // 有备份一体机应用并且运行状态为正常，初始化界面则不提供一键安装备份一体机
                                    const backupApplicationResult = data.some((item: Applicaion) => item.appType === ApplicationAppTypeE.DATA_BACKUP &&
                                        item.status === ApplicationRunningStatusE.RUNNING);

                                    this.localStorageService.set(STORAGE_KEY.BACKUP_APPLICATION_CHECK, backupApplicationResult);
                                } else {
                                    this.localStorageService.set(STORAGE_KEY.ANTIRANSOMWARE_APPLICATION_CHECK, false);
                                    this.localStorageService.set(STORAGE_KEY.BACKUP_APPLICATION_CHECK, false);
                                }

                                this.eventService.emit('updateAntiransomwareFlag');
                                return of(null);
                            }),
                        );
                } else {
                    this.localStorageService.set(STORAGE_KEY.ANTIRANSOMWARE_APPLICATION_CHECK, false);
                    this.localStorageService.set(STORAGE_KEY.BACKUP_APPLICATION_CHECK, false);
                    return of(null);
                }
            }),
        );
    }

    /**
     * 远端设备是否正常。健康状态Normal、运行状态link_up。
     */
    isRemoteDeviceNormal(remoteDevice: RemoteDevice): boolean {
        return remoteDevice &&
            Number(remoteDevice.HEALTHSTATUS) === healthStatusE.Normal.value &&
            Number(remoteDevice.RUNNINGSTATUS) === runningStatusE.LINK_UP.value;
    }

    private getCountRest(rest): Rest {
        // 生成count查询的url
        const countRest = cloneDeep(rest);
        // count不下发selectFields字段
        countRest.setParams({ selectFields: undefined });

        (countRest as any).baseUrl = rest.baseUrl + (rest.type === 'IBASE' ? '/count' : '.count()');

        return countRest;
    }

    private getData(rest: Rest, count: number, options: GetAllDataOptions): Observable<any[]> {
        // 如果数量为0，返回空数组
        if (!count) {
            return of([]);
        }

        // 根据是否传入远端设备ID判断是否是远端执行
        const remote = typeof options.remoteDeviceId === 'number';
        const cmds: Array<Observable<HttpResponse>> = [];
        const results: any[] = [];

        for (let offset = 0; offset < count; offset += options.pageSize) {
            const rangeStr = rest.type === 'IBASE' ?
                `[${offset}-${offset + options.pageSize}]` :
                `{"offset":${offset},"limit":${options.pageSize}}`;

            rest.setTempParams({
                range: rangeStr,
            });

            const action = remote ?
                this.remoteExe.get(options.remoteDeviceId, rest, { ...options, doException: false }) :
                this.http.get(rest, { ...options, doException: false });

            cmds.push(action);
        }

        return Observable.create((observer) => {
            this.extend.forkOrder(cmds)
                .subscribe({
                    next: (resArr: HttpResponse[]) => {
                        resArr.forEach((res) => results.push(...(res.data || [])));
                        observer.next(results);
                        observer.complete();
                    },
                    error: (forkOrderErr) => {
                        this.http.handleException(rest, forkOrderErr.err);
                        observer.error(forkOrderErr.err);
                    },
                });
        });
    }
}
